import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { path } from 'ramda';
import { fmtzDate, isBillingDev } from '../../common/ambient';
import { 
  subStatusDisplay, 
  displayPriceForSubscription, 
  subIsPlus,
  billingDeferSubscription
} from './';
import './AdminUserSubscriptions.less';

function AdminUserSubscriptions({ 
  user, 
  subscriptions, 
  onRefreshUser, 
  showActions = true 
}) {
  const [extendSubscription, setExtendSubscription] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [extendingSubscription, setExtendingSubscription] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const userSubs = subscriptions || path(['payments', 'subscriptions'], user) || [];
  const customerId = path(['payments', 'customerId'], user);
  
  if (userSubs.length === 0 && !customerId) return null;

  const handleExtendInputChange = (e) => {
    setExtendDays(e.target.value);
  };
  
  const toggleExtendSubscription = (sub) => {
    setExtendSubscription(prevSub => prevSub === sub ? null : sub);
  };
  
  const submitExtendSubscription = async () => {
    if (!extendSubscription || !extendDays || extendDays <= 0) return;
    
    setExtendingSubscription(true);
    
    try {
      const result = await billingDeferSubscription(
        user.email,
        parseInt(extendDays, 10),
        extendSubscription.productName
      );
      
      if (result.success && result.message && result.message.includes('successfully deferred')) {
        // Set to "processing" state with successful message
        setExtendingSubscription('success');
        setSuccessMessage(`${extendSubscription.nickname || extendSubscription.productName} extended by ${extendDays} days!`);
        
        // Wait a few seconds for webhooks to process before refreshing
        setTimeout(() => {
          if (onRefreshUser) onRefreshUser();
          setExtendSubscription(null);
          setExtendDays(30);
          setExtendingSubscription(false);
          setSuccessMessage(null);
        }, 3000); // 3 second delay
      } else {
        // Show appropriate error message
        const errorMessage = result.message || 'Failed to extend subscription. Please try again.';
        alert(errorMessage);
        setExtendingSubscription(false);
      }
    } catch (error) {
      console.error('Error extending subscription:', error);
      alert('An error occurred while extending the subscription.');
      setExtendingSubscription(false);
    }
  };

  // Generate Stripe dashboard URL
  const getStripeDashboardUrl = () => {
    if (!customerId) return null;
    
    const baseUrl = 'https://dashboard.stripe.com';
    const path = isBillingDev() ? '/test/customers/' : '/customers/';
    
    return baseUrl + path + customerId;
  };
  
  const stripeDashboardUrl = getStripeDashboardUrl();

  return (
    <div className="payment-admin-user-subscriptions">
      {stripeDashboardUrl && (
        <div className="stripe-customer-link">
          <a href={stripeDashboardUrl} target="_blank" rel="noopener noreferrer">
            View in Stripe Dashboard {customerId}
          </a>
        </div>
      )}
      
      {userSubs.length > 0 && (
        <table className='subscriptions-table'>
          <thead>
            <tr>
              <th>Applies To</th>
              <th>Product</th>
              <th>Billing Period</th>
              <th>Status</th>
              <th>Renews/Expires</th>
              <th>Price</th>
              {showActions && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {userSubs.map((sub, i) => {
              const isAwnPlusSub = /awn-plus-account-(monthly|yearly)/.test(sub.productName);
              const isExtendingSub = extendSubscription && extendSubscription.productName === sub.productName;
              
              return (
                <tr key={i}>
                  <td>{sub.macAddress || 'Account'}</td>
                  <td>{sub.nickname || sub.productName}</td>
                  <td>{/monthly/.test(sub.productName) ? 'Monthly' : 'Annual'}</td>
                  <td>{subStatusDisplay ? subStatusDisplay(sub) : sub.status}</td>
                  <td>{sub.currentPeriodEnd ? fmtzDate('MMM D, YYYY')(sub.currentPeriodEnd * 1000) : '--'}</td>
                  <td>{displayPriceForSubscription ? displayPriceForSubscription(sub) : '--'}</td>
                  {showActions && (
                    <td>
                      {!isExtendingSub ? (
                        <button 
                          className='btn btn-sm btn-primary extend-btn'
                          onClick={() => toggleExtendSubscription(sub)}
                        >
                          Extend
                        </button>
                      ) : (
                        <div className='extend-subscription-form'>
                          {successMessage ? (
                            <div className='success-message'>{successMessage}</div>
                          ) : (
                            <>
                              <div className='extend-inputs'>
                                <input
                                  type='number'
                                  value={extendDays}
                                  onChange={handleExtendInputChange}
                                  className='extend-days-input'
                                  min='1'
                                />
                                <span className='days-label'>days</span>
                              </div>
                              <div className='extend-actions'>
                                <button 
                                  className='btn btn-sm btn-primary'
                                  onClick={submitExtendSubscription}
                                  disabled={extendingSubscription !== false}
                                >
                                  {extendingSubscription === true ? 'Extending...' : 
                                    extendingSubscription === 'success' ? 'Success!' : 
                                    extendingSubscription === 'processing' ? 'Processing...' : 'Submit'}
                                </button>
                                <button 
                                  className='btn btn-sm btn-default'
                                  onClick={() => toggleExtendSubscription(null)}
                                  disabled={extendingSubscription !== false}
                                >
                                  Cancel
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

AdminUserSubscriptions.propTypes = {
  user: PropTypes.object.isRequired,
  subscriptions: PropTypes.array,
  onRefreshUser: PropTypes.func,
  showActions: PropTypes.bool
};

AdminUserSubscriptions.defaultProps = {
  showActions: true
};

export default AdminUserSubscriptions;

AdminUserSubscriptions.displayName = 'AdminUserSubscriptions'
